import { supabase } from '../config/supabase'

/**
 * Obtiene el wedding_id del usuario (considerando si es partner)
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<string|null>}
 */
const getUserWeddingId = async (userId) => {
  try {
    // Primero, obtener el perfil del usuario para verificar si es partner
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError)
    }

    // Determinar el user_id de la boda:
    // - Si el usuario tiene partner_user_id (ES el partner), buscar la boda del usuario principal (partner_user_id)
    // - Si no tiene partner_user_id (ES el principal), buscar su propia boda (userId)
    // - También verificar si hay otro usuario que tiene partner_user_id apuntando a este usuario
    let weddingOwnerId = userId
    
    if (profile?.partner_user_id) {
      // El usuario ES el partner, buscar la boda del usuario principal
      weddingOwnerId = profile.partner_user_id
    } else {
      // El usuario podría ser el principal, pero verificar si hay un partner que apunta a él
      // Si hay un partner, ambos deberían ver la misma boda (la del principal)
      // Por ahora, usar el userId del usuario actual
      weddingOwnerId = userId
    }

    const { data, error } = await supabase
      .from('weddings')
      .select('id')
      .eq('user_id', weddingOwnerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle() // Usar maybeSingle() en lugar de single() para evitar 406

    if (error) {
      // Si no hay boda, devolver null
      if (error.code === 'PGRST116') {
        return null
      }
      console.error('Error fetching wedding ID:', error)
      return null
    }

    return data?.id || null
  } catch (error) {
    console.error('Error fetching wedding ID:', error)
    return null
  }
}

/**
 * Obtiene el countdown hasta la fecha del evento
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<{days: number, hours: number, minutes: number, seconds: number}>}
 */
export const getCountdown = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    const { data, error } = await supabase
      .from('weddings')
      .select('event_date')
      .eq('id', weddingId)
      .single()

    if (error) throw error

    if (!data || !data.event_date) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    // Crear fecha de evento - asegurar que incluya la hora (medianoche si no se especifica)
    const eventDate = new Date(data.event_date)
    // Si la fecha no tiene hora, establecerla a medianoche del día del evento
    if (data.event_date && !data.event_date.includes('T')) {
      eventDate.setHours(0, 0, 0, 0)
    }
    
    const now = new Date()
    const diff = eventDate.getTime() - now.getTime()

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    // Calcular correctamente días, horas, minutos y segundos
    const totalSeconds = Math.floor(diff / 1000)
    const days = Math.floor(totalSeconds / (60 * 60 * 24))
    const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60))
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
    const seconds = totalSeconds % 60

    return { days, hours, minutes, seconds }
  } catch (error) {
    console.error('Error fetching countdown:', error)
    return { days: 0, hours: 0, minutes: 0, seconds: 0 }
  }
}

/**
 * Obtiene las estadísticas del checklist
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<{completed: number, pending: number, thisMonth: number}>}
 */
export const getChecklistStats = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return { completed: 0, pending: 0, thisMonth: 0 }
    }

    const { data, error } = await supabase
      .from('tasks')
      .select('is_completed, due_date')
      .eq('wedding_id', weddingId)

    if (error) throw error

    const completed = data?.filter(item => item.is_completed === true).length || 0
    const pending = data?.filter(item => item.is_completed === false).length || 0
    
    // Tareas de este mes
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    
    const thisMonth = data?.filter(item => {
      if (!item.due_date) return false
      const dueDate = new Date(item.due_date)
      return dueDate >= startOfMonth && dueDate <= endOfMonth && !item.is_completed
    }).length || 0

    return { completed, pending, thisMonth }
  } catch (error) {
    console.error('Error fetching checklist stats:', error)
    return { completed: 0, pending: 0, thisMonth: 0 }
  }
}

/**
 * Obtiene las estadísticas del budget
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<{spent: number, remaining: number, total: number, percentage: number}>}
 */
export const getBudgetStats = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return { spent: 0, remaining: 0, total: 0, percentage: 0 }
    }

    // Obtener el presupuesto del usuario actual (debe estar sincronizado con el principal)
    // Ambos usuarios (principal y partner) deben tener el mismo presupuesto
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('budget, partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError) {
      // Si es un error 406 o PGRST116 (no encontrado), usar 0 como presupuesto
      if (profileError.code === 'PGRST116' || profileError.status === 406) {
        return { spent: 0, remaining: 0, total: 0, percentage: 0 }
      }
      throw profileError
    }

    let totalBudget = parseFloat(profileData?.budget || 0)
    
    // Si el usuario es partner y no tiene presupuesto, obtenerlo del principal y sincronizar
    if (!totalBudget && profileData?.partner_user_id) {
      const { data: principalProfile, error: principalError } = await supabase
        .from('profiles')
        .select('budget')
        .eq('id', profileData.partner_user_id)
        .maybeSingle()
      
      if (principalError && principalError.code !== 'PGRST116') {
        console.warn('Error fetching principal budget:', principalError)
      }
      
      if (principalProfile?.budget) {
        totalBudget = parseFloat(principalProfile.budget)
        // Sincronizar en background
        supabase
          .from('profiles')
          .update({ budget: totalBudget })
          .eq('id', userId)
          .then(() => console.log('✅ Presupuesto sincronizado en partner'))
          .catch(err => console.warn('Error syncing budget:', err))
      }
    }

    // Obtener los items de presupuesto
    const { data: budgetItemsData, error: budgetItemsError } = await supabase
      .from('budget_items')
      .select('actual_cost, estimated_cost')
      .eq('wedding_id', weddingId)

    if (budgetItemsError) throw budgetItemsError

    // Sumar los costos actuales, si no hay costo actual usar el estimado
    const spent = budgetItemsData?.reduce((sum, item) => {
      const cost = item.actual_cost || item.estimated_cost || 0
      return sum + parseFloat(cost)
    }, 0) || 0

    const remaining = totalBudget - spent
    const percentage = totalBudget > 0 ? Math.round((spent / totalBudget) * 100) : 0

    return { spent, remaining, total: totalBudget, percentage }
  } catch (error) {
    console.error('Error fetching budget stats:', error)
    return { spent: 0, remaining: 0, total: 0, percentage: 0 }
  }
}

/**
 * Obtiene las estadísticas de RSVP
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<{confirmed: number, total: number}>}
 */
export const getRSVPStats = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return { confirmed: 0, total: 0 }
    }

    const { data, error } = await supabase
      .from('guests')
      .select('rsvp_status')
      .eq('wedding_id', weddingId)

    if (error) throw error

    const total = data?.length || 0
    const confirmed = data?.filter(guest => guest.rsvp_status === 'confirmed').length || 0

    return { confirmed, total }
  } catch (error) {
    console.error('Error fetching RSVP stats:', error)
    return { confirmed: 0, total: 0 }
  }
}

/**
 * Obtiene el progreso del timeline
 * @param {string} userId - ID del usuario
 * @returns {Promise<{configured: boolean, percentage: number}>}
 */
export const getTimelineProgress = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return { configured: false, percentage: 0 }
    }

    const { data, error } = await supabase
      .from('timeline_events')
      .select('id')
      .eq('wedding_id', weddingId)

    if (error) throw error

    // Si hay eventos en el timeline, consideramos que está configurado
    const hasEvents = data && data.length > 0
    // Porcentaje basado en si está configurado (90% si tiene eventos, 0% si no)
    const percentage = hasEvents ? 90 : 0

    return { configured: hasEvents, percentage }
  } catch (error) {
    console.error('Error fetching timeline progress:', error)
    return { configured: false, percentage: 0 }
  }
}

/**
 * Obtiene el total de tareas
 * @param {string} userId - ID del usuario
 * @returns {Promise<number>}
 */
export const getTotalTasks = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return 0
    }

    const { data, error } = await supabase
      .from('tasks')
      .select('id')
      .eq('wedding_id', weddingId)

    if (error) throw error

    return data?.length || 0
  } catch (error) {
    console.error('Error fetching total tasks:', error)
    return 0
  }
}

/**
 * Obtiene todos los datos del dashboard de una vez
 * @param {string} userId - ID del usuario
 * @returns {Promise<{countdown: object, checklist: object, budget: object, rsvp: object, timeline: object, totalTasks: number}>}
 */
export const getDashboardData = async (userId) => {
  try {
    const [countdown, checklist, budget, rsvp, timeline, totalTasks] = await Promise.all([
      getCountdown(userId),
      getChecklistStats(userId),
      getBudgetStats(userId),
      getRSVPStats(userId),
      getTimelineProgress(userId),
      getTotalTasks(userId)
    ])

    return { countdown, checklist, budget, rsvp, timeline, totalTasks }
  } catch (error) {
    console.error('Error fetching dashboard data:', error)
    return {
      countdown: { days: 0, hours: 0, minutes: 0, seconds: 0 },
      checklist: { completed: 0, pending: 0, thisMonth: 0 },
      budget: { spent: 0, remaining: 0, total: 0, percentage: 0 },
      rsvp: { confirmed: 0, total: 0 },
      timeline: { configured: false, percentage: 0 },
      totalTasks: 0
    }
  }
}
